import React, { useEffect, useMemo, useState } from 'react'
import {
  fetchChecklist, submitTicketAnswers, closeTicket, updateTicketStatus, assignOperator, visibleQuestions, isNoLabel, CNC_DASHBOARD_URL, stageMeta,
} from './remoteUnlockApi'
import { DEST, A12_NOTE, suggestedDest, sendToOptionFor } from './stage2Rules'
import * as stage2Rules from './stage2Rules'
import * as stage3Rules from './stage3Rules'
import ChecklistFields, { missingAnswers, answerSummary } from './ChecklistFields'
import { RuModal, RuChip } from './RuPrimitives'

/** visible questions + answers → POST answers payload */
function toPayload(visible, answers) {
  return visible.map(q => (
    q.options.length
      ? { question: q.id, option: answers[q.id] }
      : { question: q.id, option: null, description: answers[q.id] }
  ))
}

function SummaryBox({ title, pills }) {
  if (!pills.length) return null
  return (
    <div className="ru-pills-box">
      <span aria-hidden>▣</span>
      <div>
        <div className="ru-summary-title" style={{ marginBottom: 0 }}>{title}</div>
        <div className="ru-chips">{pills.map(p => <RuChip key={p.key} ok={p.ok}>{p.label}</RuChip>)}</div>
      </div>
    </div>
  )
}

const WarnNote = ({ note }) => (
  <div className="ru-note-warn"><span aria-hidden>⚠</span><div><b>{note.label}</b> {note.text}</div></div>
)

/**
 * Fill form for one stage (agent has access to this level).
 * Questions: GET get-checklist?checklist_category=<stage>&sub_category_slug=…
 * Save:      POST ticket-checklist-answer/<ticket_id>/ { checklist_stage, answers }
 * Stage 2 follows the journey rules in stage2Rules.js (visibility, hints, popups).
 */
/** stage → frontend journey rules (from CRM_Interactive_Journey_V15.html) */
const JOURNEY_RULES = { l2: stage2Rules, l3: stage3Rules }

export default function StageForm({ fetchJson, ticketId, stage, subCategorySlug, stationCode, previous, onSaved }) {
  const meta = stageMeta(stage)
  const stageName = meta.title.split(':')[0]
  const [questions, setQuestions] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [answers, setAnswers] = useState({})
  const [showErrors, setShowErrors] = useState(false)
  const [modal, setModal] = useState(null) // 'confirm' | 'call_dropped'
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  useEffect(() => {
    if (!fetchJson || !stage) return
    let active = true
    setLoading(true); setLoadError(''); setAnswers({}); setShowErrors(false)
    fetchChecklist(fetchJson, stage, subCategorySlug)
      .then(qs => { if (active) setQuestions(qs) })
      .catch(err => { if (active) { setQuestions([]); setLoadError(err?.message || 'Failed to load checklist.') } })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [fetchJson, stage, subCategorySlug])

  /* Stage 2 journey rules apply when the L2 checklist questions are recognised */
  // journey rules apply when the stage's checklist questions are recognised; otherwise move_next / generic
  const rules = JOURNEY_RULES[stage] || null
  const jmap = useMemo(() => (rules ? rules.identify(questions) : null), [rules, questions])
  const visible = jmap ? rules.visible(jmap, answers) : visibleQuestions(questions, answers)
  const hints = jmap ? rules.hints(jmap, answers) : {}
  const qNotes = jmap ? rules.notes(jmap) : {}
  const context = jmap ? rules.contextLine(previous?.l2 || []) : null
  const summary = answerSummary(questions, answers, visible)
  const noCount = summary.filter(s => s.isChoice && isNoLabel(s.label)).length
  const outcome = jmap && modal === 'confirm' ? rules.submitOutcome(jmap, answers) : null
  const drop = jmap && modal === 'call_dropped' ? rules.dropOutcome(jmap, answers) : null
  const pills = jmap ? rules.pills(jmap, answers) : []

  const setAnswer = (qid, value) => {
    setShowErrors(false)
    setAnswers(prev => {
      let next = { ...prev, [qid]: value }
      if (jmap) {
        // Stage 2: reason picked → pre-select the suggested "Send this case to"
        if (stage === 'l2' && jmap.reason && qid === jmap.reason.id && jmap.sendTo) {
          const sug = suggestedDest(jmap, next)
          next[jmap.sendTo.id] = sug ? sendToOptionFor(jmap, sug) : ''
        }
        // drop answers of questions that are no longer shown (upstream answer changed)
        const keep = new Set(rules.visible(jmap, next).map(q => q.id))
        next = Object.fromEntries(Object.entries(next).filter(([k, v]) => keep.has(k) && v !== ''))
      }
      return next
    })
  }

  const handleSubmit = () => {
    if (!visible.length || missingAnswers(questions, answers, visible).length) { setShowErrors(true); return }
    setSaveError('')
    setModal('confirm')
  }

  /*
   * Popup buttons (Submit and Call Dropped):
   *   Close Ticket             → POST the checklist first, then status 'closed'
   *   Assign to Callback Queue → status 'call_dropped'; checklist NOT saved (agent refills it on callback)
   *   Assign to Operator       → POST the checklist first, then GET assign-operator/<ticket_id>/
   *   Assign to CNC            → POST the Stage 2 checklist (moves the case on to L3)
   *   null (non-L2 stages)     → POST the checklist
   */
  const MESSAGES = {
    CLOSE: `${stageName} checklist submitted and ticket closed.`,
    CALLBACK: 'Call dropped — ticket moved to the Stage II callback queue. The checklist must be filled again on callback.',
    OPERATOR: `${stageName} checklist submitted and ticket assigned to the Station Operator.`,
    CNC: `${stageName} checklist submitted — case assigned to Stage III · CNC.`,
  }

  const runAction = async (dest) => {
    setSaving(true); setSaveError('')
    try {
      // answered, visible questions → checklist payload
      const answered = visible.filter(q => String(answers[q.id] ?? '').trim())
      const submitChecklist = () => submitTicketAnswers(fetchJson, ticketId, stage, toPayload(answered, answers))

      if (dest === 'CLOSE') {
        // 1) submit the checklist (skipped only when nothing was answered), 2) close the ticket
        if (answered.length) await submitChecklist()
        await closeTicket(fetchJson, ticketId)
      } else if (dest === 'CALLBACK') {
        await updateTicketStatus(fetchJson, ticketId, 'call_dropped')
      } else if (dest === 'OPERATOR') {
        // 1) submit the checklist (skipped only when nothing was answered), 2) assign to operator
        if (answered.length) await submitChecklist()
        await assignOperator(fetchJson, ticketId)
      } else {
        // CNC (or a non-L2 stage): submitting the checklist hands the case to L3
        await submitChecklist()
      }
      setModal(null)
      if (dest === 'CALLBACK') setAnswers({})
      const msg = dest ? MESSAGES[dest] : `${stageName} checklist submitted.`
      onSaved?.(msg, dest !== 'CALLBACK')   // refreshes the ticket + checklist; callback shows an amber banner
    } catch (err) {
      setSaveError(err?.message || 'Action failed. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const closeModal = () => { if (!saving) setModal(null) }

  return (
    <div className="ru-stage-body">
      {stage === 'l2' && (
        <div className="ru-cnc">
          <span className="ru-cnc-icon" aria-hidden>🖥</span>
          <div className="ru-cnc-text">
            <div className="ru-cnc-title">Open station page on CNC remote-unlock dashboard</div>
            <div className="ru-cnc-sub">Station {stationCode || '—'}</div>
          </div>
          <a className="ru-btn ru-btn-primary" href={CNC_DASHBOARD_URL} target="_blank" rel="noopener noreferrer">Open CNC ↗</a>
        </div>
      )}

      {context && (
        <div className="ru-context">{context.html[0]}<b>{context.html[1]}</b></div>
      )}

      {loading && <div className="ru-muted">Loading checklist…</div>}
      {!loading && loadError && <div className="ru-error">{loadError}</div>}
      {!loading && !loadError && questions.length === 0 && (
        <div className="ru-muted">No {meta.level} checklist is configured for this sub-category.</div>
      )}

      {!loading && (
        <ChecklistFields
          questions={questions} answers={answers} showErrors={showErrors} namePrefix={stage}
          visible={visible} hints={hints} notes={qNotes} onChange={setAnswer}
        />
      )}
      {showErrors && <div className="ru-error">Please answer all visible checks before submitting.</div>}

      {questions.length > 0 && (
        <div className="ru-actions">
          <button type="button" className="ru-btn ru-btn-ghost" onClick={() => { setAnswers({}); setShowErrors(false) }}>Cancel</button>
          {stage !== 'l1' && (
            <button type="button" className="ru-btn ru-btn-danger" onClick={() => { setSaveError(''); setModal('call_dropped') }}>{rules?.dropButtonLabel || '📞 Call Dropped'}</button>
          )}
          <button type="button" className="ru-btn ru-btn-violet" onClick={handleSubmit}>Submit</button>
        </div>
      )}

      {/* ── Submit: Stage 2 journey outcome ── */}
      {outcome && (
        <RuModal
          tone={outcome.tone} title={outcome.title} subtitle={outcome.subtitle} onClose={closeModal}
          footer={<>
            <button type="button" className="ru-btn ru-btn-ghost" onClick={closeModal} disabled={saving}>Review Checklist</button>
            {outcome.actions.map(d => (
              <button key={d} type="button" className={`ru-btn ${DEST[d].btn}`} onClick={() => runAction(d)} disabled={saving}>
                {saving ? 'Please wait…' : DEST[d].action}
              </button>
            ))}
          </>}
        >
          <SummaryBox title={rules?.summaryTitle || 'Summary'} pills={pills} />
          <div className="ru-outcome-body">
            {outcome.body.map((line, i) => (
              <div key={i} className={line.warn ? 'warn' : ''}>{line.label && <b>{line.label} </b>}{line.text}</div>
            ))}
          </div>
          {(outcome.notes || []).map((n, i) => <WarnNote key={i} note={n} />)}
          {saveError && <div className="ru-error">{saveError}</div>}
        </RuModal>
      )}

      {/* ── Submit: other stages (generic review) ── */}
      {modal === 'confirm' && !jmap && (
        <RuModal
          tone={noCount ? 'warning' : 'success'}
          title={noCount ? `${stageName} — ${noCount} check${noCount > 1 ? 's' : ''} not met` : `✓ ${stageName} checks passed`}
          subtitle="Review the answers before submitting."
          onClose={closeModal}
          footer={<>
            <button type="button" className="ru-btn ru-btn-ghost" onClick={closeModal} disabled={saving}>Review Checklist</button>
            <button type="button" className="ru-btn ru-btn-primary" onClick={() => runAction(null)} disabled={saving}>
              {saving ? 'Submitting…' : 'Confirm & Submit'}
            </button>
          </>}
        >
          <div className={`ru-summary ${noCount ? 'warn' : 'ok'}`}>
            <div className="ru-summary-title">{meta.level} Summary</div>
            <div className="ru-answer-list">
              {summary.map((s, i) => (
                <div key={s.id} className="ru-answer-row">
                  <span className="ru-answer-num">{i + 1}</span>
                  <span className="ru-answer-q">{s.text}</span>
                  <span className={`ru-chip ${!s.isChoice ? 'neutral' : isNoLabel(s.label) ? 'bad' : 'ok'}`}>{s.label}</span>
                </div>
              ))}
            </div>
          </div>
          {saveError && <div className="ru-error">{saveError}</div>}
        </RuModal>
      )}

      {/* ── Call Dropped ── */}
      {modal === 'call_dropped' && (
        <RuModal
          tone="warning" width={560}
          title="📞 Call dropped"
          subtitle={drop ? drop.subtitle : `${stageName} — call dropped mid-session`}
          onClose={closeModal}
          footer={<>
            <button type="button" className="ru-btn ru-btn-ghost" onClick={closeModal} disabled={saving}>Back</button>
            <button
              type="button" className={`ru-btn ${DEST[drop ? drop.dest : 'CALLBACK'].btn}`}
              onClick={() => runAction(drop ? drop.dest : 'CALLBACK')} disabled={saving}
            >
              {saving ? 'Please wait…' : DEST[drop ? drop.dest : 'CALLBACK'].action}
            </button>
          </>}
        >
          <p className="ru-text" style={{ marginBottom: 0 }}>
            {drop ? drop.body : `The call dropped mid-session. The case goes to the ${DEST.CALLBACK.label} for a re-attempt and the QIS session lock is released.`}
          </p>
          <WarnNote note={A12_NOTE} />
          {saveError && <div className="ru-error">{saveError}</div>}
        </RuModal>
      )}
    </div>
  )
}
